import { useEffect, useRef, useState } from "react";
import {
  XMarkIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  TrashIcon,
  StopCircleIcon,
} from "@heroicons/react/24/outline";
import { SparklesIcon } from "@heroicons/react/24/solid";
import katex from "katex";
import "katex/dist/katex.min.css";
import useCopilotStore from "../../store/copilotStore";
import useProjectStore from "../../store/projectStore";

// ── Math renderers ────────────────────────────────────────────────────────────

function LatexInline({ math }) {
  try {
    const html = katex.renderToString(math, { throwOnError: false, displayMode: false });
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  } catch {
    return <code className="bg-gray-100 px-1 rounded text-[11px] font-mono">{math}</code>;
  }
}

function LatexBlock({ math }) {
  try {
    const html = katex.renderToString(math, { throwOnError: false, displayMode: true });
    return (
      <div className="overflow-x-auto py-2 text-center"
           dangerouslySetInnerHTML={{ __html: html }} />
    );
  } catch {
    return <pre className="bg-gray-100 p-2 rounded text-xs font-mono">{math}</pre>;
  }
}

// ── Lightweight markdown renderer ────────────────────────────────────────────

// Inline: **bold**, `code`, $math$
function InlineText({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\$[^$\n]+\$)/g);
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**"))
          return <strong key={i}>{p.slice(2, -2)}</strong>;
        if (p.startsWith("`") && p.endsWith("`"))
          return (
            <code key={i} className="bg-gray-100 text-bh-700 px-1 rounded text-[11px] font-mono">
              {p.slice(1, -1)}
            </code>
          );
        if (p.startsWith("$") && p.endsWith("$") && p.length > 2)
          return <LatexInline key={i} math={p.slice(1, -1)} />;
        return p;
      })}
    </>
  );
}

// Detect inline follow-up suggestion blocks the LLM sometimes writes as text instead of calling the tool.
// Strips the block from the message and returns the questions as an array.
function extractInlineSuggestions(text) {
  const pattern = /\n*#{1,4}\s*Follow[- ]?up Suggestion[s]?:?\n([\s\S]*)$/i;
  const match = text.match(pattern);
  if (!match) return { cleanText: text, suggestions: [] };
  const cleanText = text.slice(0, match.index).trim();
  const suggestions = match[1]
    .split("\n")
    .map(l => { const m = l.match(/^[\d]+\.\s+(.+)/) || l.match(/^[-*]\s+(.+)/); return m?.[1]?.trim(); })
    .filter(Boolean);
  return { cleanText, suggestions };
}

function normalizeMath(text) {
  // \[...\] → $$...$$  (display math)
  text = text.replace(/\\\[\s*([\s\S]*?)\s*\\\]/g, (_, m) => `$$${m.trim()}$$`);
  // \(...\) → $...$    (inline math)
  text = text.replace(/\\\(\s*([\s\S]*?)\s*\\\)/g, (_, m) => `$${m.trim()}$`);
  return text;
}

function renderMarkdown(rawText) {
  const text = normalizeMath(rawText);
  // Split by fenced code blocks and display math blocks first
  const blocks = text.split(/(```[\s\S]*?```|\$\$[\s\S]*?\$\$)/g);
  const out = [];

  blocks.forEach((block, bi) => {
    if (block.startsWith("```")) {
      const body = block.slice(3, -3).replace(/^\w*\n/, "");
      out.push(
        <pre key={`cb-${bi}`} className="bg-gray-900 text-green-300 text-[11px] font-mono rounded-lg p-3 my-2 overflow-x-auto whitespace-pre-wrap">
          {body}
        </pre>
      );
      return;
    }

    if (block.startsWith("$$") && block.endsWith("$$")) {
      out.push(<LatexBlock key={`lx-${bi}`} math={block.slice(2, -2).trim()} />);
      return;
    }

    const lines = block.split("\n");
    let listItems = [];
    const flush = () => {
      if (listItems.length) {
        out.push(<ul key={`ul-${bi}-${out.length}`} className="list-disc pl-4 space-y-0.5 my-1">{listItems}</ul>);
        listItems = [];
      }
    };

    lines.forEach((line, li) => {
      if (/^#{1,3}\s/.test(line)) {
        flush();
        const content = line.replace(/^#{1,3}\s/, "");
        out.push(
          <p key={`h-${bi}-${li}`} className="font-semibold text-gray-900 mt-2 mb-0.5">
            <InlineText text={content} />
          </p>
        );
      } else if (/^[-*]\s/.test(line)) {
        listItems.push(
          <li key={`li-${bi}-${li}`} className="text-gray-800">
            <InlineText text={line.slice(2)} />
          </li>
        );
      } else if (/^\d+\.\s/.test(line)) {
        flush();
        out.push(
          <p key={`ol-${bi}-${li}`} className="text-gray-800 my-0.5 pl-1">
            <InlineText text={line} />
          </p>
        );
      } else if (line.trim() === "") {
        flush();
        out.push(<div key={`sp-${bi}-${li}`} className="h-1.5" />);
      } else {
        flush();
        out.push(
          <p key={`p-${bi}-${li}`} className="text-gray-800 leading-relaxed">
            <InlineText text={line} />
          </p>
        );
      }
    });
    flush();
  });

  return out;
}

// ── Tool call → human-readable label ─────────────────────────────────────────

const _TOOL_LABELS = {
  get_project_info:      "Reading project data",
  get_design_parameters: "Reading design parameters",
  get_meco_geometry:     "Reading MECO geometry",
  get_blade_angles:      "Reading blade angles",
  get_blade_thickness:   "Reading blade thickness",
};

function ThinkingDots() {
  return (
    <span className="inline-flex gap-1 items-center shrink-0">
      {[0, 1, 2].map(i => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-bh-500 animate-bounce"
          style={{ animationDelay: `${i * 150}ms` }}
        />
      ))}
    </span>
  );
}

// ── Starter prompts ───────────────────────────────────────────────────────────

const DEFAULT_STARTERS = [
  "Guide me through my impeller blade design.",
  "What D2 and beta2 should I use for the given design's ns for a very high efficiency pump?",
  "What is the ideal suction inlet D1 for optimal cavitation performance for the given design?",
  "Explain ISO 9906 acceptance grades",
];

const API_URL = process.env.REACT_APP_API_URL || "";

// ── Main component ────────────────────────────────────────────────────────────

export default function CopilotPanel() {
  const { isOpen, togglePanel } = useCopilotStore();
  const { activeDesign, activeProject } = useProjectStore();

  const [messages,     setMessages]     = useState([]);
  const [isStreaming,  setIsStreaming]  = useState(false);
  const [toolStatus,   setToolStatus]   = useState(null);
  const [input,        setInput]        = useState("");
  const [suggestions,       setSuggestions]       = useState([]);
  const [starters,          setStarters]          = useState(DEFAULT_STARTERS);
  const [fileUploadEnabled, setFileUploadEnabled] = useState(true);

  // Fetch panel config (starter questions + feature flags)
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    fetch(`${API_URL}/api/copilot/starters`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.questions?.length) setStarters(data.questions);
        if (data?.file_upload_enabled === false) setFileUploadEnabled(false);
      })
      .catch(() => {});
  }, []);

  const bottomRef   = useRef(null);
  const textareaRef = useRef(null);
  const fileRef     = useRef(null);
  const abortRef    = useRef(null);
  // Token batching — accumulate deltas and flush at display frame rate
  const tokenBufRef = useRef("");
  const rafRef      = useRef(null);

  // Auto-scroll on new content
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
  }, [input]);

  const send = async (text) => {
    const content = text.trim();
    if (!content || isStreaming) return;
    setInput("");

    const history = messages.map(({ role, content }) => ({ role, content }));
    setSuggestions([]);

    setMessages(m => [
      ...m,
      { role: "user",      content },
      { role: "assistant", content: "", streaming: true },
    ]);
    setIsStreaming(true);
    setToolStatus("Thinking…");

    const token = localStorage.getItem("access_token");
    tokenBufRef.current = "";
    const controller = new AbortController();
    abortRef.current = controller;

    // Flush accumulated tokens into the last message at display frame rate
    const flushTokens = () => {
      rafRef.current = null;
      const text = tokenBufRef.current;
      if (!text) return;
      tokenBufRef.current = "";
      setMessages(m => {
        const copy = [...m];
        const last = { ...copy[copy.length - 1] };
        last.content = (last.content || "") + text;
        copy[copy.length - 1] = last;
        return copy;
      });
    };

    try {
      const res = await fetch(`${API_URL}/api/copilot/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          messages: [...history, { role: "user", content }],
          ...(activeDesign?.id != null ? { design_id: activeDesign.id } : {}),
          ...(activeProject?.id != null ? { project_id: activeProject.id } : {}),
        }),
        signal: controller.signal,
      });

      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const j = await res.json(); msg = j.error || msg; } catch { /* ignore */ }
        throw new Error(msg);
      }

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let done = false;

      while (!done) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;

        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop(); // keep incomplete trailing line

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") { done = true; break; }
          try {
            const parsed = JSON.parse(payload);
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.status) {
              setToolStatus(parsed.status);
            }
            if (parsed.delta) {
              setToolStatus(null);
              tokenBufRef.current += parsed.delta;
              if (!rafRef.current) {
                rafRef.current = requestAnimationFrame(flushTokens);
              }
            }
            if (parsed.tool_call) {
              const label = _TOOL_LABELS[parsed.tool_call] || parsed.tool_call.replace(/_/g, " ");
              setToolStatus(`${label}…`);
            }
            if (parsed.tool_result) {
              setToolStatus("Thinking…");
            }
            if (parsed.suggestions) {
              setSuggestions(parsed.suggestions);
            }
          } catch (e) {
            if (e.message !== "Unexpected end of JSON input") throw e;
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        setMessages(m => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          if (last?.streaming || last?.content === "") {
            copy[copy.length - 1] = {
              role: "assistant",
              content: `⚠ Error: ${e.message}`,
            };
          }
          return copy;
        });
      }
    } finally {
      abortRef.current = null;
      setToolStatus(null);
      // Flush any tokens that hadn't been painted yet
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      flushTokens();
      setIsStreaming(false);
      setMessages(m => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (!last) return copy;
        let updated = last?.streaming ? { ...last, streaming: false } : last;
        // Fallback: if the LLM wrote suggestions as text instead of calling the tool, promote them
        if (updated.role === "assistant" && updated.content) {
          const { cleanText, suggestions: inlineSuggs } = extractInlineSuggestions(updated.content);
          if (inlineSuggs.length > 0) {
            updated = { ...updated, content: cleanText };
            setSuggestions(inlineSuggs);
          }
        }
        // Drop empty assistant messages (LLM called a tool only, no text response)
        if (updated.role === "assistant" && !updated.content?.trim()) {
          return copy.slice(0, -1);
        }
        copy[copy.length - 1] = updated;
        return copy;
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const snippet = text.slice(0, 8000);
    send(`Please analyse this document and extract key engineering parameters:\n\n\`\`\`\n${snippet}\n\`\`\``);
    e.target.value = "";
  };

  const clearMessages = () => { setMessages([]); setSuggestions([]); };

  if (!isOpen) return null;

  return (
    <div className="w-full flex flex-col bg-white border-l border-gray-200 overflow-hidden">

      {/* ── Header ── */}
      <div className="px-4 py-3 bg-gray-900 flex items-center gap-2 shrink-0">
        <SparklesIcon className="h-4 w-4 text-bh-400 shrink-0" />
        <span className="text-sm font-semibold text-white flex-1">Pump Copilot</span>
        {activeProject && (
          <span className="text-[10px] bg-gray-700 text-gray-300 border border-gray-600 px-2 py-0.5 rounded-full truncate max-w-[100px]"
                title={activeProject.name}>
            {activeProject.name}
          </span>
        )}
        {activeDesign && (
          <span className="text-[10px] bg-bh-700 text-bh-100 border border-bh-600 px-2 py-0.5 rounded-full truncate max-w-[100px]"
                title={activeDesign.name}>
            {activeDesign.name}
          </span>
        )}
        {messages.length > 0 && (
          <button onClick={clearMessages} title="Clear conversation"
            className="text-gray-400 hover:text-gray-200 transition-colors">
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
        <button onClick={togglePanel} title="Close"
          className="text-gray-400 hover:text-gray-200 transition-colors">
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      {/* ── Messages ── */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 text-sm">

        {messages.length === 0 && (
          <div className="flex flex-col items-center text-center pt-8 pb-2 space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-bh-50 border border-bh-100 flex items-center justify-center">
              <SparklesIcon className="h-6 w-6 text-bh-500" />
            </div>
            <p className="font-semibold text-gray-700">Pump Engineering Copilot</p>
            <p className="text-xs text-gray-400 leading-relaxed max-w-[240px]">
              Ask about impeller sizing, blade angles, NPSH, or pump standards.
              {activeProject && <span className="block mt-1 text-gray-500">Project: <strong>{activeProject.name}</strong></span>}
              {activeDesign && <span className="block mt-0.5 text-bh-600">Design: <strong>{activeDesign.name}</strong></span>}
            </p>
            <div className="flex flex-col gap-1.5 w-full mt-2">
              {starters.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="text-xs text-left px-3 py-2 rounded-lg border border-gray-200 hover:border-bh-300 hover:bg-bh-50 text-gray-600 hover:text-bh-700 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.filter(msg => !(msg.role === "assistant" && !msg.content && msg.streaming)).map((msg, i) => (
          <div key={i}
            className={`flex flex-col gap-0.5 ${msg.role === "user" ? "items-end" : "items-start"}`}>

            <div className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm
              ${msg.role === "user"
                ? "bg-bh-600 text-white rounded-tr-sm"
                : "bg-gray-50 border border-gray-100 text-gray-800 rounded-tl-sm"}`}>
              {msg.role === "user"
                ? <span className="whitespace-pre-wrap">{msg.content}</span>
                : <div className="space-y-0.5">{renderMarkdown(msg.content)}</div>
              }
              {msg.streaming && (
                <span className="inline-block w-[3px] h-[14px] bg-bh-500 ml-0.5 align-middle animate-pulse rounded-sm" />
              )}
            </div>
          </div>
        ))}

        {toolStatus && (
          <div className="flex items-center gap-2.5 px-3.5 py-2 rounded-xl bg-bh-50 border border-bh-100 text-xs text-bh-700">
            <ThinkingDots />
            <span>{toolStatus}</span>
          </div>
        )}

        {suggestions.length > 0 && !isStreaming && (
          <div className="flex flex-col gap-1.5 pl-1">
            {suggestions.map((q) => (
              <button
                key={q}
                onClick={() => send(q)}
                className="text-xs text-left px-3 py-2 rounded-lg border border-bh-200 bg-bh-50
                  hover:border-bh-400 hover:bg-bh-100 text-bh-700 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Input ── */}
      <div className="px-3 pb-3 pt-2 border-t border-gray-100 shrink-0 space-y-1.5">
        <div className="flex items-end gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2
          focus-within:border-bh-400 focus-within:bg-white transition-colors">
          {fileUploadEnabled && (
            <button
              onClick={() => fileRef.current?.click()}
              title="Attach file (.txt, .csv, .xml)"
              className="text-gray-400 hover:text-bh-600 transition-colors shrink-0 mb-[2px]">
              <PaperClipIcon className="h-4 w-4" />
            </button>
          )}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything about pump design…"
            rows={1}
            className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none resize-none leading-5"
            style={{ minHeight: "20px", maxHeight: "128px" }}
          />
          {isStreaming ? (
            <button
              onClick={() => abortRef.current?.abort()}
              title="Stop generation"
              className="text-red-500 hover:text-red-700 transition-colors shrink-0 mb-[2px]">
              <StopCircleIcon className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={() => send(input)}
              disabled={!input.trim()}
              title="Send (Enter)"
              className="text-bh-600 hover:text-bh-700 disabled:text-gray-300 transition-colors shrink-0 mb-[2px]">
              <PaperAirplaneIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="text-[10px] text-gray-400 text-center">
          {fileUploadEnabled
            ? "Enter to send · Shift+Enter for newline · Attach .txt / .csv / .xml"
            : "Enter to send · Shift+Enter for newline"}
        </p>
        {fileUploadEnabled && (
          <input
            ref={fileRef}
            type="file"
            accept=".txt,.csv,.xml,.json"
            className="hidden"
            onChange={handleFile}
          />
        )}
      </div>
    </div>
  );
}
